import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactList from 'react-list';
import throttle from 'lodash/throttle';

import Container from '../container';
import { CircularProgress } from '../progress';

import TableHead from './TableHead';
import TableRow from './TableRow';
import './styles.scss';

const SCROLL_NEAR_BOTTOM_THRESHOLD = 1200;
const SCROLL_THROTTLE_MS = 150;

class Table extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    cols: PropTypes.array.isRequired,
    chunks: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.string).isRequired,
    onRowTouchTap: PropTypes.func,
    sortTable: PropTypes.func,
    order: PropTypes.string,
    colSorted: PropTypes.string,
    colsToSort: PropTypes.array,
    pgmolTable: PropTypes.bool,
    isCustom: PropTypes.bool,
    rowHeight: PropTypes.number,
    onScrollNearBottom: PropTypes.func,
  };

  static defaultProps = {
    chunks: {},
    loading: false,
    onRowTouchTap: null,
    sortTable: null,
    order: 'desc',
    colSorted: null,
    colsToSort: [],
    pgmolTable: false,
    isCustom: false,
    rowHeight: undefined,
    onScrollNearBottom: null,
  };

  scrollRef = React.createRef();

  attachScrollListener = () => {
    const el = this.scrollRef.current;
    if (el && this.props.onScrollNearBottom && !this.throttledScroll) {
      this.throttledScroll = throttle(this.handleScroll, SCROLL_THROTTLE_MS);
      el.addEventListener('scroll', this.throttledScroll, { passive: true });
    }
  };

  componentDidMount() {
    this.attachScrollListener();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.loading && !this.props.loading) {
      this.attachScrollListener();
    }
  }

  componentWillUnmount() {
    const el = this.scrollRef.current;
    if (el && this.throttledScroll) {
      el.removeEventListener('scroll', this.throttledScroll);
      this.throttledScroll.cancel();
    }
  }

  handleScroll = () => {
    const el = this.scrollRef.current;
    if (!el || !this.props.onScrollNearBottom) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_NEAR_BOTTOM_THRESHOLD) {
      this.props.onScrollNearBottom();
    }
  };

  handleRowTouchTap = key => () => this.props.onRowTouchTap(key);

  renderRow = (index, key) => (
    <>
      <TableRow
        key={key}
        onClick={
          this.props.onRowTouchTap
            ? this.handleRowTouchTap(this.props.items[index])
            : null
        }
        id={this.props.items[index]}
        data={this.props.chunks[this.props.items[index]]}
        cols={this.props.cols}
      />
    </>
  );

  render() {
    const element = this.props.loading ? (
      <Container>
        <CircularProgress />
      </Container>
    ) : (
      <div className="table">
        <TableHead
          cols={this.props.cols}
          onColClick={col => this.props.sortTable(col)}
          order={this.props.order}
          colSorted={this.props.colSorted}
          colsToSort={this.props.colsToSort}
        />

        <div
          ref={this.scrollRef}
          className="table-body scroll-container"
          style={{ height: this.props.isCustom ? '55%' : '99%' }}
        >
          <ReactList
            itemRenderer={this.renderRow}
            length={this.props.items.length}
            type="uniform"
            minSize={
              this.props.pgmolTable
                ? 22
                : this.props.rowHeight != null
                  ? this.props.rowHeight
                  : 1
            }
          />
        </div>
      </div>
    );

    return element;
  }
}

export default Table;
